import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { WorkspaceService } from '@/api';
import type { WorkspaceResponse } from '@/api/generated/models/WorkspaceResponse';

// Workspace management state interface (separate from workspace selection slice)
interface WorkspaceManagementState {
  workspaces: WorkspaceResponse[];
  isLoading: boolean;
  error: string | null;
  currentPage: number;
  pageSize: number;
  totalItems: number;
}

// Initial state
const initialState: WorkspaceManagementState = {
  workspaces: [],
  isLoading: false,
  error: null,
  currentPage: 1,
  pageSize: 20,
  totalItems: 0,
};

// Async thunks
export const fetchWorkspaces = createAsyncThunk(
  'workspaceManagement/fetchWorkspaces',
  async (params: {
    orgId: string;
    q?: string;
    page?: number;
    perPage?: number;
    includeUnavailable?: boolean;
  }, { rejectWithValue }) => {
    try {
      const response = await WorkspaceService.appsWorkspaceViewsListWorkspaces(params);
      return {
        data: (response.data || []) as WorkspaceResponse[],
        meta: response.meta || { page: 1, per_page: 20, total: 0 }
      };
    } catch (error: any) {
      return rejectWithValue(error.message || '워크스페이스 목록 조회에 실패했습니다.');
    }
  }
);

export const createWorkspace = createAsyncThunk(
  'workspaceManagement/createWorkspace',
  async (workspaceData: { 
    org_id: string; 
    name: string; 
    sort_key?: number 
  }, { rejectWithValue }) => {
    try {
      const response = await WorkspaceService.appsWorkspaceViewsCreateWorkspace({
        requestBody: workspaceData
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error || '워크스페이스 생성에 실패했습니다.');
    }
  }
);

export const updateWorkspace = createAsyncThunk(
  'workspaceManagement/updateWorkspace',
  async (params: { 
    workspaceId: string; 
    data: {
      name?: string;
      sort_key?: number;
      available?: boolean;
      start_date?: string | null;
      end_date?: string | null;
      show_tasks?: boolean;
    }
  }, { rejectWithValue }) => {
    try {
      const response = await WorkspaceService.appsWorkspaceViewsUpdateWorkspace({
        workspaceId: params.workspaceId,
        requestBody: params.data
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || '워크스페이스 수정에 실패했습니다.');
    }
  }
);

export const deleteWorkspace = createAsyncThunk(
  'workspaceManagement/deleteWorkspace',
  async (workspaceId: string, { rejectWithValue }) => {
    try {
      await WorkspaceService.appsWorkspaceViewsDeleteWorkspace({ workspaceId });
      return workspaceId;
    } catch (error: any) {
      return rejectWithValue(error.message || '워크스페이스 삭제에 실패했습니다.');
    }
  }
);

export const fetchJoinedWorkspaces = createAsyncThunk(
  'workspaceManagement/fetchJoinedWorkspaces',
  async (params: { 
    page?: number; 
    perPage?: number 
  }, { rejectWithValue }) => {
    try {
      const response = await WorkspaceService.appsWorkspaceViewsListJoinedWorkspaces(params);
      return {
        data: (response.data || []) as WorkspaceResponse[],
        meta: response.meta || { page: 1, per_page: 20, total: 0 }
      };
    } catch (error: any) {
      return rejectWithValue(error.message || '참여한 워크스페이스 목록 조회에 실패했습니다.');
    }
  }
);

// Workspace management slice
const workspaceManagementSlice = createSlice({
  name: 'workspaceManagement',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch workspaces
      .addCase(fetchWorkspaces.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWorkspaces.fulfilled, (state, action) => {
        state.isLoading = false;
        state.workspaces = action.payload.data;
        state.currentPage = action.payload.meta.page;
        state.pageSize = action.payload.meta.per_page;
        state.totalItems = action.payload.meta.total;
        state.error = null;
      })
      .addCase(fetchWorkspaces.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create workspace
      .addCase(createWorkspace.fulfilled, (state, action) => {
        state.workspaces.unshift(action.payload);
      })
      .addCase(createWorkspace.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Update workspace
      .addCase(updateWorkspace.fulfilled, (state, action) => {
        const index = state.workspaces.findIndex(w => w.id === action.payload.id);
        if (index !== -1) {
          state.workspaces[index] = action.payload;
        }
      })
      .addCase(updateWorkspace.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Delete workspace
      .addCase(deleteWorkspace.fulfilled, (state, action) => {
        state.workspaces = state.workspaces.filter(w => w.id !== action.payload);
      })
      .addCase(deleteWorkspace.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Fetch joined workspaces
      .addCase(fetchJoinedWorkspaces.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchJoinedWorkspaces.fulfilled, (state, action) => {
        state.isLoading = false;
        state.workspaces = action.payload.data;
        state.currentPage = action.payload.meta.page;
        state.pageSize = action.payload.meta.per_page;
        state.totalItems = action.payload.meta.total;
        state.error = null;
      })
      .addCase(fetchJoinedWorkspaces.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setCurrentPage, setPageSize } = workspaceManagementSlice.actions;
export default workspaceManagementSlice.reducer;